import React, { useState, useEffect, useCallback } from 'react';
import {
  getCommentThread,
  commentReplyById,
  changeHideCommentStatus,
  deleteComment,
} from '../../api/comment';
import { useSocial } from '../../context/socialProvider';
import UnifiedCommentDto from '../../types/comment.types';
import ThreadComment from './ThreadComment';
import { platform } from 'os';
import DeleteCommentConfirmation from './DeleteCommentConfirmation';

interface ThreadResponse {
  success: boolean;
  data: {
    platformCommentId: string;
    platformComment: UnifiedCommentDto;
    replies: UnifiedCommentDto[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      hasMore: boolean;
    };
  };
}

interface CommentThreadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  platformCommentId: string;
  originalComment: UnifiedCommentDto;
}

interface NestedRepliesState {
  [commentId: string]: {
    replies: UnifiedCommentDto[];
    isLoading: boolean;
    hasMore: boolean;
    page: number;
  };
}

const CommentThreadPopup: React.FC<CommentThreadPopupProps> = ({
  isOpen,
  onClose,
  platformCommentId,
  originalComment,
}) => {
  // State management
  const [threadData, setThreadData] = useState<ThreadResponse['data'] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mainReplyText, setMainReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nestedReplies, setNestedReplies] = useState<NestedRepliesState>({});
  const [activeReplyInput, setActiveReplyInput] = useState<string | null>(null);
  const [replyTexts, setReplyTexts] = useState<{ [commentId: string]: string }>(
    {}
  );
  const [hidingComments, setHidingComments] = useState<Set<string>>(new Set());
  const [deletingComments, setDeletingComments] = useState<Set<string>>(
    new Set()
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    comment: UnifiedCommentDto | null;
  }>({
    isOpen: false,
    comment: null,
  });

  const { currentSocial } = useSocial();

  // Load thread data when popup opens
  useEffect(() => {
    if (isOpen && platformCommentId && currentSocial?.id) {
      loadThreadData(1);
    }
  }, [isOpen, platformCommentId, currentSocial?.id]);

  // Load main thread data
  const loadThreadData = async (page: number = 1) => {
    if (!currentSocial?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getCommentThread(
        Number(currentSocial.id),
        platformCommentId,
        page,
        10
      );

      if (response?.success) {
        if (page === 1) {
          setThreadData(response.data);
        } else {
          // Append new replies for pagination
          setThreadData((prev) =>
            prev
              ? {
                  ...prev,
                  replies: [...prev.replies, ...response.data.replies],
                  pagination: response.data.pagination,
                }
              : response.data
          );
        }
        setCurrentPage(page);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load thread');
      console.error('Error loading thread:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load nested replies for a specific comment
  const loadNestedReplies = async (commentId: string, page: number = 1) => {
    if (!currentSocial?.id) return;

    // Update loading state
    setNestedReplies((prev) => ({
      ...prev,
      [commentId]: {
        replies: prev[commentId]?.replies || [],
        page: prev[commentId]?.page || 0,
        hasMore: prev[commentId]?.hasMore || false,
        isLoading: true,
      },
    }));

    try {
      const response = await getCommentThread(
        Number(currentSocial.id),
        commentId,
        page,
        10 // Smaller page size for nested replies
      );

      if (response?.success) {
        setNestedReplies((prev) => {
          const existing = prev[commentId] || {
            replies: [],
            page: 0,
            hasMore: false,
            isLoading: false,
          };
          return {
            ...prev,
            [commentId]: {
              replies:
                page === 1
                  ? response.data.replies
                  : [...existing.replies, ...response.data.replies],
              isLoading: false,
              hasMore: response.data.pagination.hasMore,
              page: page,
            },
          };
        });
      }
    } catch (err: any) {
      console.error('Error loading nested replies:', err);
      setNestedReplies((prev) => ({
        ...prev,
        [commentId]: {
          replies: prev[commentId]?.replies || [],
          page: prev[commentId]?.page || 0,
          hasMore: prev[commentId]?.hasMore || false,
          isLoading: false,
        },
      }));
    }
  };

  // Handle reply submission
  const handleReply = async (targetCommentId?: string) => {
    const text = targetCommentId ? replyTexts[targetCommentId] : mainReplyText;
    const commentIdToReply = targetCommentId || platformCommentId;

    if (!text?.trim() || !currentSocial?.id || isReplying) return;

    setIsReplying(true);

    try {
      await commentReplyById(commentIdToReply, text, Number(currentSocial.id));

      if (targetCommentId) {
        // Clear the specific reply text and hide input
        setReplyTexts((prev) => ({ ...prev, [targetCommentId]: '' }));
        setActiveReplyInput(null);
        // Reload nested replies for this comment
        await loadNestedReplies(targetCommentId, 1);
      } else {
        // Clear main reply text
        setMainReplyText('');
        // Reload the main thread
        await loadThreadData(1);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send reply');
      console.error('Error sending reply:', err);
    } finally {
      setIsReplying(false);
    }
  };

  // Handle reply text changes
  const updateReplyText = (commentId: string, text: string) => {
    setReplyTexts((prev) => ({ ...prev, [commentId]: text }));
  };

  // Show reply input for a specific comment
  const showReplyInput = (commentId: string) => {
    setActiveReplyInput(commentId);
  };

  // Hide reply input
  const hideReplyInput = () => {
    setActiveReplyInput(null);
  };

  // Load more main replies
  const loadMoreReplies = () => {
    if (threadData?.pagination.hasMore) {
      loadThreadData(currentPage + 1);
    }
  };

  // Load more nested replies for a specific comment
  const loadMoreNestedReplies = (commentId: string) => {
    const current = nestedReplies[commentId];
    if (current?.hasMore) {
      loadNestedReplies(commentId, current.page + 1);
    }
  };

  const onHideComment = async (
    platformCommentId: string,
    hide: boolean,
    socialId: number
  ) => {
    // Store original state for potential rollback
    const originalThreadData = threadData;
    const originalNestedReplies = nestedReplies;

    try {
      // Add to loading set for visual feedback
      setHidingComments((prev) => new Set([...prev, platformCommentId]));

      // OPTIMISTIC UPDATE - Update UI immediately
      setThreadData((prevData) => {
        if (!prevData) return prevData;

        // Check if it's the main platform comment
        if (prevData.platformComment.platformCommentId === platformCommentId) {
          return {
            ...prevData,
            platformComment: {
              ...prevData.platformComment,
              hidden: hide,
            },
          };
        }

        // Check if it's in the main replies
        const updatedReplies = prevData.replies.map((reply) =>
          reply.platformCommentId === platformCommentId
            ? { ...reply, hidden: hide }
            : reply
        );

        return {
          ...prevData,
          replies: updatedReplies,
        };
      });

      // OPTIMISTIC UPDATE - Update nested replies immediately
      setNestedReplies((prevNested) => {
        const updatedNested = { ...prevNested };

        // Loop through all nested reply groups
        Object.keys(updatedNested).forEach((commentId) => {
          const nestedData = updatedNested[commentId];
          const updatedNestedReplies = nestedData.replies.map((reply) =>
            reply.platformCommentId === platformCommentId
              ? { ...reply, hidden: hide }
              : reply
          );

          // Only update if there was a change
          if (
            JSON.stringify(updatedNestedReplies) !==
            JSON.stringify(nestedData.replies)
          ) {
            updatedNested[commentId] = {
              ...nestedData,
              replies: updatedNestedReplies,
            };
          }
        });

        return updatedNested;
      });

      // Make the API call
      const result = await changeHideCommentStatus(
        platformCommentId,
        hide,
        socialId
      );

      if (!result.success) {
        // ROLLBACK - Revert to original state if API fails
        console.error('Failed to hide comment:', result.error);
        setThreadData(originalThreadData);
        setNestedReplies(originalNestedReplies);

        // Optional: Show error message to user
        // setError('Failed to update comment visibility');
      }
    } catch (error) {
      console.error('Error hiding comment:', error);

      // ROLLBACK - Revert to original state on error
      setThreadData(originalThreadData);
      setNestedReplies(originalNestedReplies);

      // Optional: Show error message to user
      // setError('Failed to update comment visibility');
    } finally {
      // Remove from loading set
      setHidingComments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(platformCommentId);
        return newSet;
      });
    }
  };

  const onDeleteComment = useCallback(
    async (comment: UnifiedCommentDto) => {
      if (!currentSocial?.id) return;

      try {
        const platformCommentId = comment.platformCommentId;

        // Add to deleting set
        setDeletingComments((prev) => new Set([...prev, platformCommentId]));

        // Make the API call and wait for response
        await deleteComment(platformCommentId, Number(currentSocial.id));

        // SUCCESS - Remove the comment from UI
        setThreadData((prevData) => {
          if (!prevData) return prevData;

          // Check if it's the main platform comment
          if (
            prevData.platformComment.platformCommentId === platformCommentId
          ) {
            // If main comment is deleted, we should probably close the popup
            // or show a "Comment deleted" message
            onClose(); // Close the popup since main comment is gone
            return prevData;
          }

          // Remove from main replies
          const filteredReplies = prevData.replies.filter(
            (reply) => reply.platformCommentId !== platformCommentId
          );

          return {
            ...prevData,
            replies: filteredReplies,
          };
        });

        // Remove from nested replies
        setNestedReplies((prevNested) => {
          const updatedNested = { ...prevNested };

          // Remove the comment from any nested reply groups
          Object.keys(updatedNested).forEach((commentId) => {
            const nestedData = updatedNested[commentId];
            const filteredNestedReplies = nestedData.replies.filter(
              (reply) => reply.platformCommentId !== platformCommentId
            );

            // Update if there was a change
            if (filteredNestedReplies.length !== nestedData.replies.length) {
              updatedNested[commentId] = {
                ...nestedData,
                replies: filteredNestedReplies,
              };
            }
          });

          // Also remove if this comment had its own nested replies
          if (updatedNested[platformCommentId]) {
            delete updatedNested[platformCommentId];
          }

          return updatedNested;
        });

        // Close confirmation modal
        setDeleteConfirmation({ isOpen: false, comment: null });
      } catch (error) {
        console.error('Error deleting comment:', error);
        // Optional: Show error toast/notification to user
        // setError('Failed to delete comment. Please try again.');
      } finally {
        // Remove from deleting set
        setDeletingComments((prev) => {
          const newSet = new Set(prev);
          newSet.delete(comment.platformCommentId);
          return newSet;
        });
      }
    },
    [currentSocial?.id, onClose]
  );

  // Add these helper functions:
  const showDeleteConfirmation = useCallback((comment: UnifiedCommentDto) => {
    setDeleteConfirmation({
      isOpen: true,
      comment,
    });
  }, []);

  const hideDeleteConfirmation = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      comment: null,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirmation.comment) {
      onDeleteComment(deleteConfirmation.comment);
    }
  }, [deleteConfirmation.comment, onDeleteComment]);

  // Render a thread comment and its nested replies recursively
  const renderComment = (comment: UnifiedCommentDto, indentLevel: number) => {
    const commentId = comment.platformCommentId;
    const nestedData = nestedReplies[commentId];
    const isShowingReplyInput = activeReplyInput === commentId;
    const replyText = replyTexts[commentId] || '';
    const hasLoadedReplies =
      indentLevel === 0 ? true : Boolean(nestedData?.replies?.length);
    const isHidingThisComment = hidingComments.has(commentId);
    const isDeletingThisComment = deletingComments.has(commentId);

    return (
      <ThreadComment
        key={commentId}
        comment={comment}
        indentLevel={indentLevel}
        isReplying={isReplying}
        replyText={replyText}
        showReplyInput={isShowingReplyInput}
        hasLoadedReplies={hasLoadedReplies}
        onReplyTextChange={(text) => updateReplyText(commentId, text)}
        onReplySubmit={() => handleReply(commentId)}
        onReplyCancel={hideReplyInput}
        onShowReplyInput={() => showReplyInput(commentId)}
        onLoadMoreReplies={() => {
          // If no nested replies loaded yet, load them. Otherwise load more.
          if (!hasLoadedReplies) {
            loadNestedReplies(commentId, 1);
          } else {
            loadMoreNestedReplies(commentId);
          }
        }}
        hasMoreReplies={nestedData?.hasMore}
        isLoadingReplies={nestedData?.isLoading}
        onHideComment={() =>
          onHideComment(
            comment.platformCommentId,
            !comment.hidden,
            Number(currentSocial?.id)
          )
        }
        onDeleteComment={() => showDeleteConfirmation(comment)}
        isDeletingComment={isDeletingThisComment}
      >
        {/* Render nested replies if they exist */}
        {nestedData?.replies?.map((nestedReply) =>
          renderComment(nestedReply, indentLevel + 1)
        )}
      </ThreadComment>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-grey rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Comment Thread</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl font-light"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && !threadData ? (
            <div className="text-center text-gray-400 py-8">
              Loading thread...
            </div>
          ) : error ? (
            <div className="text-center text-red-400 py-8">{error}</div>
          ) : threadData ? (
            <div className="space-y-4">
              {/* Original Comment (Top Level) */}
              {renderComment(threadData.platformComment, 0)}

              {/* Main Thread Replies */}
              <div className="space-y-3">
                {threadData.replies.map((reply) => renderComment(reply, 1))}
              </div>

              {/* Load More Main Replies Button */}
              {threadData.pagination.hasMore && (
                <div className="text-center">
                  <button
                    onClick={loadMoreReplies}
                    disabled={isLoading}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium disabled:opacity-50"
                  >
                    {isLoading ? 'Loading...' : 'Load more replies'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Main Reply Input */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex space-x-3">
            <div className="flex-1">
              <textarea
                value={mainReplyText}
                onChange={(e) => setMainReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="w-full bg-greythree text-white rounded-lg p-3 resize-none border border-gray-600 focus:border-violet focus:outline-none"
                rows={2}
              />
            </div>
            <button
              onClick={() => handleReply()}
              disabled={!mainReplyText.trim() || isReplying}
              className={`px-4 py-2 bg-yellow hover:bg-opacity-80 disabled:bg-gray-600 disabled:opacity-50 rounded-lg font-medium h-fit ${
                !mainReplyText ? 'text-white' : 'text-black'
              }`}
            >
              {isReplying ? 'Sending...' : 'Reply'}
            </button>
          </div>
        </div>
      </div>
      <DeleteCommentConfirmation
        isOpen={deleteConfirmation.isOpen}
        comment={deleteConfirmation.comment}
        isDeleting={
          deleteConfirmation.comment
            ? deletingComments.has(deleteConfirmation.comment.platformCommentId)
            : false
        }
        onConfirm={confirmDelete}
        onCancel={hideDeleteConfirmation}
      />
    </div>
  );
};

export default CommentThreadPopup;
